import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/ui.css';

export default function ActionMenu({ items = [], ariaLabel = 'Acciones' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const MENU_WIDTH = 240;
  const OFFSET_RIGHT = 8;
  const OFFSET_Y = 8;
  const MAX_HEIGHT = 420;

  useEffect(() => {
    const handler = (e) => {
      const clickedMenu = menuRef.current && menuRef.current.contains(e.target);
      const clickedTrigger = ref.current && ref.current.contains(e.target);
      if (!clickedMenu && !clickedTrigger) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useLayoutEffect(() => {
    const computePosition = () => {
      if (!open) return;
      const trigger = ref.current?.querySelector('.ui-action-trigger');
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const estimatedHeight = Math.min(MAX_HEIGHT, (items?.filter(i => i.type !== 'divider' && i.type !== 'section').length || 1) * 42 + 80);
      const spaceBelow = viewportHeight - rect.bottom - OFFSET_Y;
      const openUpwards = spaceBelow < estimatedHeight;
      const left = Math.max(8, Math.min(rect.right - MENU_WIDTH + OFFSET_RIGHT, viewportWidth - MENU_WIDTH - 8));
      const style = {
        position: 'fixed',
        width: `${MENU_WIDTH}px`,
        left: `${left}px`,
        maxHeight: `${MAX_HEIGHT}px`,
        overflowY: 'auto',
        zIndex: 2000,
        top: openUpwards ? 'auto' : `${rect.bottom + OFFSET_Y}px`,
        bottom: openUpwards ? `${viewportHeight - rect.top + OFFSET_Y}px` : 'auto'
      };
      setMenuStyle(style);
    };

    computePosition();
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [open, items]);

  const handleClick = (item) => {
    if (item.disabled || item.type === 'divider' || item.type === 'section') return;
    setOpen(false);
    item.onClick?.();
  };

  return (
    <div className="ui-action-menu" ref={ref}>
      <button
        type="button"
        className="ui-action-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        &#8942;
      </button>
      {open && createPortal(
        (
          <div className="ui-action-list" role="menu" style={menuStyle} ref={menuRef}>
            {items.map((item, idx) => {
              if (item.type === 'divider') return <div className="ui-action-divider" key={`div-${idx}`} />;
              if (item.type === 'section') {
                return (
                  <div className="ui-action-section" key={`sec-${idx}`}>
                    {item.label}
                  </div>
                );
              }
              return (
                <button
                  key={item.key || idx}
                  type="button"
                  className={`ui-action-item ${item.variant === 'danger' ? 'danger' : ''}`}
                  onClick={() => handleClick(item)}
                  role="menuitem"
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        ),
        document.body
      )}
    </div>
  );
}
